'use client';

import {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useChat } from '@ai-sdk/react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useAISDKRuntime, AssistantChatTransport } from '@assistant-ui/react-ai-sdk';
import { Thread } from '../assistant-ui/thread';
import { ToolUIProvider } from '../assistant-ui/tool-ui-provider';
import { useMomento } from '@/hooks/useMomento';
import { cn } from '@/lib/utils';
import { MomentoThreadList } from './MomentoThreadList';
import { Button } from '@/components/ui/button';
import { PanelLeftIcon } from 'lucide-react';
import { MemosList } from '@/components/memo/MemosList';
import { PostsList } from '@/components/momento/PostsList';
import { MomentoContextBlock } from '@/components/momento/MomentoContextBlock';
import { ComposerContextProvider, Attachment } from '@/components/assistant-ui/composer-context';
import { buildContextItems } from './momento-context-items';

interface MomentsChatInterfaceProps {
  userId: string;
  domainId: string;
  className?: string;
  onReady?: (sendMessage: (message: string) => Promise<void>) => void;
  showThreadList?: boolean;
  onToggleThreadList?: () => void;
  onToggleMemosPreview?: () => void;
  memoCount?: number;
  onTogglePostsPreview?: () => void;
  postCount?: number;
  autoMessage?: string | null;
  onAutoMessageSent?: () => void;
}

export interface MomentsChatInterfaceMethods {
  handleImageSelect: (files: FileList) => Promise<void>;
}

// History endpoint returns {role, content: [parts]}; useChat wants AI SDK v5
// UIMessage {role, parts}. File parts are filtered downstream anyway.
function toUIMessages(legacy: any[]): any[] {
  return legacy.map((m) => {
    const parts = Array.isArray(m.content)
      ? m.content
          .filter((p: any) => p && p.type === 'text')
          .map((p: any) => ({ type: 'text' as const, text: p.text ?? '' }))
      : [{ type: 'text' as const, text: typeof m.content === 'string' ? m.content : '' }];
    return {
      id: m.id ?? crypto.randomUUID(),
      role: m.role,
      parts: parts.length > 0 ? parts : [{ type: 'text' as const, text: '' }],
    };
  });
}

export const MomentsChatInterface = forwardRef<MomentsChatInterfaceMethods, MomentsChatInterfaceProps>(
  function MomentsChatInterface(
    { userId, domainId, className, showThreadList = false, onToggleThreadList, onToggleMemosPreview, memoCount = 0, onTogglePostsPreview, postCount = 0, autoMessage, onAutoMessageSent },
    ref,
  ) {
    const { selectedMoment, activeMemo } = useMomento();

    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [isThreadListVisible, setIsThreadListVisible] = useState(showThreadList);
    const [initialMessages, setInitialMessages] = useState<any[]>([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);

    // Refs hold latest values for the transport's prepareSendMessagesRequest,
    // which closes over the initial render.
    const selectedMomentRef = useRef(selectedMoment);
    const activeMemoRef = useRef(activeMemo);
    const pendingAttachmentsRef = useRef(pendingAttachments);
    useEffect(() => {
      selectedMomentRef.current = selectedMoment;
      activeMemoRef.current = activeMemo;
      pendingAttachmentsRef.current = pendingAttachments;
    });

    useEffect(() => setIsThreadListVisible(showThreadList), [showThreadList]);

    // Pick most recent thread on mount.
    useEffect(() => {
      if (!userId || !domainId || activeThreadId) return;
      (async () => {
        try {
          const res = await fetch(`/api/threads?userId=${userId}&domainId=${domainId}`);
          if (!res.ok) throw new Error('Failed to fetch threads');
          const { threads } = await res.json();
          setActiveThreadId(threads?.[0]?.id ?? `momento-${userId}-${domainId}`);
        } catch (error) {
          console.error('[MomentoChatInterface] Init thread failed:', error);
          setActiveThreadId(`momento-${userId}-${domainId}`);
        }
      })();
    }, [userId, domainId, activeThreadId]);

    // ChatRuntime is keyed on activeThreadId so it remounts when threads switch.
    useEffect(() => {
      if (!activeThreadId) return;
      setHistoryLoaded(false);
      (async () => {
        try {
          const res = await fetch(`/api/threads/${activeThreadId}/messages`);
          const { messages } = res.ok ? await res.json() : { messages: [] };
          setInitialMessages(toUIMessages(messages ?? []));
        } catch (error) {
          console.error('[MomentoChatInterface] Load history failed:', error);
          setInitialMessages([]);
        } finally {
          setHistoryLoaded(true);
        }
      })();
    }, [activeThreadId]);

    const threadListRefreshRef = useRef<(() => void) | null>(null);

    const handleNewThread = useCallback(async () => {
      try {
        const res = await fetch('/api/threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, domainId, title: 'New Conversation' }),
        });
        if (!res.ok) throw new Error('Failed to create thread');
        const { threadId } = await res.json();
        setActiveThreadId(threadId);
        threadListRefreshRef.current?.();
      } catch (error) {
        console.error('[MomentoChatInterface] Create thread failed:', error);
        alert('Failed to create new conversation');
      }
    }, [userId, domainId]);

    const handleToggleThreadList = useCallback(() => {
      setIsThreadListVisible((v) => !v);
      onToggleThreadList?.();
    }, [onToggleThreadList]);

    const handleImageSelect = useCallback(async (files: FileList) => {
      if (!activeThreadId) return;
      for (const file of Array.from(files)) {
        const tempId = crypto.randomUUID();
        setPendingAttachments((prev) => [...prev, { id: tempId, type: 'image', file, name: file.name, status: 'uploading' }]);
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('domainId', domainId);
          formData.append('threadId', activeThreadId);
          const res = await fetch('/api/chat/upload-image', { method: 'POST', body: formData });
          if (!res.ok) {
            const error = await res.json();
            throw new Error(error.error || 'Upload failed');
          }
          const { url } = await res.json();
          setPendingAttachments((prev) => prev.map((a) => (a.id === tempId ? { ...a, url, status: 'ready' as const } : a)));
        } catch (error) {
          console.error('[MomentoChatInterface] Image upload failed:', error);
          setPendingAttachments((prev) => prev.map((a) => (a.id === tempId ? { ...a, status: 'error' as const } : a)));
        }
      }
    }, [activeThreadId, domainId]);

    const handleRemoveAttachment = useCallback((id: string) => setPendingAttachments((prev) => prev.filter((a) => a.id !== id)), []);
    const handleVideoUpload = useCallback(() => onTogglePostsPreview?.(), [onTogglePostsPreview]);
    const clearPendingAttachments = useCallback(() => setPendingAttachments([]), []);

    useImperativeHandle(ref, () => ({ handleImageSelect }), [handleImageSelect]);

    const contextItems = useMemo(() => buildContextItems(selectedMoment, activeMemo), [selectedMoment, activeMemo]);

    if (!activeThreadId) {
      return (
        <div className={cn('flex h-full bg-background rounded-lg border relative items-center justify-center', className)}>
          <div className="text-muted-foreground">Loading conversation...</div>
        </div>
      );
    }

    return (
      <div className={cn('flex h-full bg-background rounded-lg border relative', className)}>
        {isThreadListVisible && (
          <MomentoThreadList
            userId={userId}
            domainId={domainId}
            activeThreadId={activeThreadId}
            onThreadSelect={setActiveThreadId}
            onNewThread={handleNewThread}
            onRefreshCallbackSet={(refreshFn) => { threadListRefreshRef.current = refreshFn; }}
          />
        )}

        <div className="flex-1 flex flex-col relative">
          <div className="absolute top-2 left-2 z-10">
            <Button variant="ghost" size="sm" onClick={handleToggleThreadList} className="h-8 w-8 p-0" title={isThreadListVisible ? 'Hide threads' : 'Show threads'}>
              <PanelLeftIcon className="h-4 w-4" />
            </Button>
          </div>

          {(onToggleMemosPreview || onTogglePostsPreview) && (
            <div className="absolute top-2 right-24 z-10 flex flex-row gap-1.5">
              {onToggleMemosPreview && <MemosList onTogglePreview={onToggleMemosPreview} memoCount={memoCount} />}
              {onTogglePostsPreview && <PostsList onTogglePreview={onTogglePostsPreview} postCount={postCount} />}
            </div>
          )}

          {contextItems.length > 0 && (
            <div className="border-b bg-muted/10 pl-12 pr-24 py-2 flex-shrink-0">
              <MomentoContextBlock items={contextItems} defaultOpen={true} className="mb-0" />
            </div>
          )}

          <div className="flex-1 flex flex-col overflow-hidden">
            {historyLoaded ? (
              <ChatRuntime
                key={activeThreadId}
                threadId={activeThreadId}
                initialMessages={initialMessages}
                userId={userId}
                domainId={domainId}
                selectedMomentRef={selectedMomentRef}
                activeMemoRef={activeMemoRef}
                pendingAttachmentsRef={pendingAttachmentsRef}
                onSendStart={clearPendingAttachments}
                autoMessage={autoMessage}
                onAutoMessageSent={onAutoMessageSent}
              >
                <ComposerContextProvider
                  value={{
                    pendingAttachments,
                    onImageSelect: handleImageSelect,
                    onRemoveAttachment: handleRemoveAttachment,
                    onVideoUpload: handleVideoUpload,
                  }}
                >
                  <ToolUIProvider>
                    <Thread />
                  </ToolUIProvider>
                </ComposerContextProvider>
              </ChatRuntime>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">Loading messages...</div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

interface ChatRuntimeProps {
  children: ReactNode;
  threadId: string;
  initialMessages: any[];
  userId: string;
  domainId: string;
  selectedMomentRef: MutableRefObject<any>;
  activeMemoRef: MutableRefObject<any>;
  pendingAttachmentsRef: MutableRefObject<Attachment[]>;
  onSendStart: () => void;
  autoMessage?: string | null;
  onAutoMessageSent?: () => void;
}

function ChatRuntime({
  children,
  threadId,
  initialMessages,
  userId,
  domainId,
  selectedMomentRef,
  activeMemoRef,
  pendingAttachmentsRef,
  onSendStart,
  autoMessage,
  onAutoMessageSent,
}: ChatRuntimeProps) {
  const onSendStartRef = useRef(onSendStart);
  useEffect(() => { onSendStartRef.current = onSendStart; });

  const transport = useMemo(
    () =>
      new AssistantChatTransport({
        api: '/api/agents/chat',
        prepareSendMessagesRequest: ({ messages, id }) => {
          const ready = pendingAttachmentsRef.current
            .filter((a) => a.status === 'ready')
            .map((a) => ({ type: a.type, url: a.url!, name: a.name }));
          onSendStartRef.current();
          return {
            body: {
              messages,
              userId,
              domainId,
              threadId: id ?? threadId,
              selectedMoment: selectedMomentRef.current,
              activeMemo: activeMemoRef.current,
              attachments: ready.length > 0 ? ready : undefined,
            },
          };
        },
      }),
    [userId, domainId, threadId, selectedMomentRef, activeMemoRef, pendingAttachmentsRef],
  );

  const chat = useChat({
    id: threadId,
    messages: initialMessages,
    // Cast: `ai` is duplicated in the dep tree (6.0.116 via @ai-sdk/react,
    // 6.0.185 direct) so AssistantChatTransport's nominal type doesn't match
    // useChat's expected ChatTransport. Same major version — fine at runtime.
    transport: transport as any,
    onError: (err) => console.error('[MomentoChatInterface] Chat error:', err),
  });

  const runtime = useAISDKRuntime(chat);

  // Auto-send from "View in Momento" — short delay lets the runtime mount.
  useEffect(() => {
    if (!autoMessage) return;
    const timer = setTimeout(() => {
      try {
        const composer = (runtime.thread as any)?.composer;
        if (composer?.setText && composer?.send) {
          composer.setText(autoMessage);
          composer.send();
        }
      } catch (error) {
        console.error('[MomentoChatInterface] Auto-send failed:', error);
      }
      onAutoMessageSent?.();
    }, 500);
    return () => clearTimeout(timer);
  }, [autoMessage, runtime, onAutoMessageSent]);

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
